import { useEffect, useMemo, useState, useCallback } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { apiGet, apiPost } from "@/lib/api";
import { AppShell } from "@/components/AppShell";
import { ProgressBar } from "@/components/ProgressBar";
import { classAccent } from "@/lib/brand";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { ChevronLeft, CheckCircle2, Lock, BookOpen, ExternalLink } from "lucide-react";

export default function ClassView() {
  const { slug } = useParams();
  const navigate = useNavigate();
  const [cls, setCls] = useState(null);
  const [loading, setLoading] = useState(true);
  const [selected, setSelected] = useState(null); // {weekId, dayId}
  const [answers, setAnswers] = useState({});
  const [saving, setSaving] = useState(false);

  const accent = classAccent(cls?.slug || slug, 0, cls?.color);

  const load = useCallback(async () => {
    try {
      const data = await apiGet(`/classes/${slug}`);
      setCls(data);
      const map = {};
      Object.values(data.answers || {}).forEach((a) => { map[a.question_id] = a.answer_text; });
      setAnswers(map);
      const firstWeek = data.weeks?.[0];
      const firstDay = firstWeek?.days?.[0];
      if (firstDay) setSelected((cur) => cur || { weekId: firstWeek.id, dayId: firstDay.id });
    } catch (err) {
      toast.error(err.message);
    } finally {
      setLoading(false);
    }
  }, [slug]);

  useEffect(() => { load(); }, [load]);

  const { week, day } = useMemo(() => {
    if (!cls || !selected) return {};
    const w = cls.weeks.find((x) => x.id === selected.weekId);
    const d = w?.days.find((x) => x.id === selected.dayId);
    return { week: w, day: d };
  }, [cls, selected]);

  const saveAnswer = async (questionId, text) => {
    if (day?.completed) return;
    setSaving(true);
    try {
      await apiPost("/answers", { question_id: questionId, answer_text: text });
    } catch (err) {
      toast.error(err.message);
    } finally {
      setSaving(false);
    }
  };

  const preReadingAnswered = (d) => {
    const pre = (d?.questions || []).filter((q) => q.type === "pre_reading");
    return pre.every((q) => (answers[q.id] || "").trim().length > 0);
  };

  const allAnswered = (d) => (d?.questions || []).every((q) => (answers[q.id] || "").trim().length > 0);

  const markComplete = async () => {
    try {
      await apiPost("/progress/complete", { day_id: day.id });
      toast.success("Day complete! 🎉");
      await load();
    } catch (err) {
      toast.error(err.message);
    }
  };

  const nav = [{ to: "/home", label: "My Classes" }];

  if (loading)
    return (
      <AppShell nav={nav}>
        <div className="h-96 rounded-3xl border-2 border-ink bg-white/60 animate-pulse" />
      </AppShell>
    );

  if (!cls) return null;

  const devotionalLocked = day && !preReadingAnswered(day);

  return (
    <AppShell nav={nav}>
      <button onClick={() => navigate("/home")} className="flex items-center gap-1 text-sm font-bold mb-5 press" data-testid="back-to-classes">
        <ChevronLeft size={16} /> All classes
      </button>

      <div className="rounded-3xl border-2 border-ink p-6 mb-6 shadow-pop" style={{ background: accent.bg, color: accent.fg }}>
        {cls.theme && (
          <span className="inline-block text-xs font-bold uppercase tracking-wider px-3 py-1 rounded-full border-2 border-ink mb-3" style={{ background: accent.tag, color: "#0F1116" }} data-testid="classview-theme">
            {cls.theme}
          </span>
        )}
        <h1 className="text-3xl font-extrabold">{cls.title}</h1>
        <p className="font-reading opacity-90 mt-1 mb-4">{cls.description}</p>
        <div className="max-w-md">
          <div className="flex justify-between text-xs font-bold mb-1">
            <span>{cls.completed_count}/{cls.day_count} days</span>
            <span>{cls.progress}%</span>
          </div>
          <ProgressBar value={cls.progress} color="#0F1116" />
        </div>
      </div>

      <div className="grid lg:grid-cols-[320px_1fr] gap-6">
        {/* Week / day index */}
        <div className="space-y-4" data-testid="week-index">
          {cls.weeks.map((w) => (
            <div key={w.id} className="rounded-2xl border-2 border-ink bg-white overflow-hidden">
              <div className="px-4 py-3 border-b-2 border-ink" style={{ background: "#FFFDF5" }}>
                <div className="text-[11px] font-bold uppercase tracking-wide text-muted-foreground">Week {w.week_number}</div>
                <div className="font-bold text-sm">{w.title}</div>
              </div>
              <div className="p-2">
                {w.days.map((d) => {
                  const active = selected?.dayId === d.id;
                  return (
                    <button
                      key={d.id}
                      onClick={() => setSelected({ weekId: w.id, dayId: d.id })}
                      data-testid={`day-nav-${d.day_number}`}
                      className={`w-full flex items-center gap-3 px-3 py-2 rounded-xl text-left press ${active ? "shadow-pop-sm" : ""}`}
                      style={active ? { background: accent.bg, color: accent.fg } : {}}
                    >
                      <span className="h-6 w-6 shrink-0 grid place-items-center rounded-full border-2 border-ink text-xs font-bold" style={{ background: d.completed ? "#B8FF3B" : "#fff", color: "#0F1116" }}>
                        {d.completed ? "✓" : d.day_number}
                      </span>
                      <span className="text-sm font-semibold truncate">{d.title}</span>
                    </button>
                  );
                })}
                {w.days.length === 0 && <p className="px-3 py-2 text-xs text-muted-foreground">No days yet.</p>}
              </div>
            </div>
          ))}
          {cls.weeks.length === 0 && (
            <div className="rounded-2xl border-2 border-ink bg-white p-6 text-center text-sm text-muted-foreground">
              This class has no content yet. Check back soon!
            </div>
          )}
        </div>

        {/* Day content */}
        <div data-testid="day-content">
          {!day ? (
            <div className="rounded-3xl border-2 border-ink bg-white p-10 text-center text-muted-foreground">
              Select a day to begin.
            </div>
          ) : (
            <div className="rounded-3xl border-2 border-ink bg-white shadow-pop p-6 sm:p-8 animate-rise">
              {week?.opening_prayer && selected?.dayId === week.days[0]?.id && (
                <div className="mb-6 rounded-2xl border-2 border-ink p-4" style={{ background: "#FFFDF5" }}>
                  <div className="text-xs font-bold uppercase tracking-wide mb-1">Opening prayer</div>
                  <p className="font-reading italic">{week.opening_prayer}</p>
                </div>
              )}

              <div className="flex items-center justify-between mb-1">
                <span className="text-xs font-bold uppercase tracking-wide text-muted-foreground">
                  Week {week?.week_number} · Day {day.day_number}
                </span>
                {day.completed && (
                  <span className="flex items-center gap-1 text-xs font-bold" style={{ color: "#31B8FF" }}>
                    <CheckCircle2 size={14} /> Completed
                  </span>
                )}
              </div>
              <h2 className="text-2xl font-extrabold mb-4">{day.title}</h2>

              {day.scripture_reference && (
                <a
                  href={`https://www.bible.com/search/bible?q=${encodeURIComponent(day.scripture_reference)}`}
                  target="_blank" rel="noreferrer"
                  className="inline-flex items-center gap-2 mb-6 px-4 py-2 rounded-full border-2 border-ink font-bold text-sm press"
                  style={{ background: "#B8FF3B" }}
                  data-testid="scripture-link"
                >
                  <BookOpen size={15} /> {day.scripture_reference} <ExternalLink size={13} />
                </a>
              )}

              {/* Pre-reading questions */}
              {day.questions.filter((q) => q.type === "pre_reading").map((q) => (
                <QField key={q.id} q={q} answers={answers} setAnswers={setAnswers} save={saveAnswer} disabled={day.completed} label="Before you read" />
              ))}

              {/* Devotional */}
              {day.devotional && (
                <div className="my-6">
                  <div className="text-xs font-bold uppercase tracking-wide mb-2 flex items-center gap-2">
                    Devotional {devotionalLocked && <Lock size={13} />}
                  </div>
                  {devotionalLocked ? (
                    <div className="rounded-2xl border-2 border-dashed border-ink p-5 text-sm text-muted-foreground" data-testid="devotional-locked">
                      Answer the questions above to unlock the devotional.
                    </div>
                  ) : (
                    <p className="font-reading text-[17px] leading-relaxed whitespace-pre-wrap" data-testid="devotional-text">
                      {day.devotional}
                    </p>
                  )}
                </div>
              )}

              {/* Reflection questions */}
              {!devotionalLocked && day.questions.filter((q) => q.type === "reflection").map((q) => (
                <QField key={q.id} q={q} answers={answers} setAnswers={setAnswers} save={saveAnswer} disabled={day.completed} label="Reflect" />
              ))}

              {!day.completed && (
                <Button
                  onClick={markComplete}
                  disabled={!allAnswered(day) || saving}
                  data-testid="mark-complete"
                  className="w-full h-12 mt-4 rounded-full border-2 border-ink shadow-pop press font-bold disabled:opacity-50"
                  style={{ background: "#0F1116", color: "#FFFDF5" }}
                >
                  {allAnswered(day) ? "Mark day complete" : "Answer all questions to complete"}
                </Button>
              )}
              {day.completed && (
                <div className="mt-4 rounded-2xl border-2 border-ink p-4 text-center text-sm font-semibold" style={{ background: "#FFFDF5" }}>
                  Answers locked — nice work on this day! 🎉
                </div>
              )}
            </div>
          )}
        </div>
      </div>
    </AppShell>
  );
}

function QField({ q, answers, setAnswers, save, disabled, label }) {
  const [local, setLocal] = useState(answers[q.id] || "");
  useEffect(() => { setLocal(answers[q.id] || ""); }, [answers, q.id]);
  return (
    <div className="mb-5">
      <div className="text-[11px] font-bold uppercase tracking-wide text-muted-foreground mb-1">{label}</div>
      <label className="block font-semibold mb-2">{q.text}</label>
      <Textarea
        value={local}
        disabled={disabled}
        onChange={(e) => setLocal(e.target.value)}
        onBlur={() => {
          setAnswers((a) => ({ ...a, [q.id]: local }));
          if (local !== (answers[q.id] || "")) save(q.id, local);
        }}
        data-testid={`answer-${q.id}`}
        rows={3}
        placeholder="Type your answer…"
        className="border-2 border-ink rounded-xl resize-none disabled:opacity-70"
      />
    </div>
  );
}
